import { useState, type ReactNode } from "react";
import { X } from "lucide-react";
import { Icon, Spinner } from "@cntrl/ui";
import { useBandContext } from "../../app/bandContext.js";
import { useOpsCalendar } from "../../app/hooks/useShows.js";
import { useMediaObjectUrl } from "../../app/hooks/useMedia.js";
import {
  useDecideShowRequest,
  useSendHoldInvites,
  useShowRequest,
  type ShowRequestAction,
  type ShowRequestView,
} from "../../app/hooks/useShowRequests.js";
import { formatMoneyCents } from "../../app/hooks/inboxFormat.js";
import { pushToast } from "../../app/toasts.js";
import { formatDay, offerDaysLeft } from "../org/rosterFormat.js";
import { ago } from "../org/bookingFunnelCards.js";
import { DetailsFrame } from "./DetailsFrame.js";
import { ShowEditorModal } from "./ShowEditorModal.js";
import { EMPTY_WHOS_PLAYING, WhosPlaying, type HoldInvitePick, type WhosPlayingDraft } from "./WhosPlaying.js";
import base from "./CalendarEventDetails.module.scss";
import { slotRoleLabel } from "./sharedShowFormat.js";
import styles from "./ShowRequestDetails.module.scss";
import { usePhoneLayout } from "../../app/phoneLayout.js";

/** Reply-by turns red at this many days or fewer — the Needs-attention rule. */
const URGENT_DAYS = 3;
/** The editor's Members section — where Hold & add crew lands on desktop. */
const MEMBERS_STEP = 3;

const HOLD_TIER = ["", "1st hold", "2nd hold", "3rd hold", "4th hold"];
const VERB: Record<string, string> = { approve: "approved", hold: "is holding", pass: "passed" };

/**
 * A SHOW REQUEST's Details (ART-327, design 4e) — what Mgmt and the artist AV open from Opportunities, Needs
 * attention or the bell. Read-only: everything is what the agent sent (the hold editor, 4d), plus a calendar check
 * so they can decide without leaving it. Same shell as every Details (`DetailsFrame`: pop-up on desktop, sheet on the
 * phone).
 *
 * The first answer wins (the server's row lock). After that everyone opening it sees who answered instead of the
 * buttons, and a late answer comes back as "Dana already approved this." rather than an error.
 *
 * Hold & add crew opens "Who's playing" in this same shell (ART-342): pick the people and their pay, then Send invites
 * answers the request as a Hold and sends them in one press. Nothing is answered before that — ‹ Details comes back
 * here with everyone kept, and Cancel leaves the request unanswered. Once a hold exists, the Decided footer's Open goes
 * to the full Booking editor on desktop (the phone, which has no editor, gets Who's playing again).
 */
export function ShowRequestDetails({ offerId, anchor = null, onClose }: {
  offerId: string;
  anchor?: DOMRect | null;
  onClose: () => void;
}) {
  const q = useShowRequest(offerId);
  const desktop = !usePhoneLayout();
  const decide = useDecideShowRequest();
  const sendInvites = useSendHoldInvites();
  const [crew, setCrew] = useState<string | null>(null);   // an existing hold's show id (the Decided footer's Open)
  const [picking, setPicking] = useState(false);           // Who's playing, before the request is answered
  const [heldShow, setHeldShow] = useState<string | null>(null);   // the hold Send made, if its invites then failed
  const [draft, setDraft] = useState<WhosPlayingDraft>(EMPTY_WHOS_PLAYING);
  const [step, setStep] = useState(MEMBERS_STEP);
  const r = q.data;
  const venue = r?.venueName || r?.buyerVenue || "Show";
  const day = r?.date ? longDay(r.date) : null;
  const dirty = draft.lines.length > 0 || draft.note.trim() !== "";

  function sent(count: number) {
    pushToast(`Hold invites sent to ${count} ${count === 1 ? "person" : "people"}.`, "success");
    onClose();
  }

  /** Send on an unanswered request: answer it as a Hold, then invite everyone to the hold that answer made. */
  function holdAndInvite(people: HoldInvitePick[], note: string | null) {
    decide.mutate({ offerId, action: "hold", reason: null }, {
      onSuccess: (res) => {
        if (res.conflict) {
          const who = res.conflict.decidedBy?.name ?? "Someone";
          const did = res.conflict.action === "pass" ? "passed on" : res.conflict.action === "hold" ? "held" : "approved";
          pushToast(`${who} already ${did} this.`);
          setPicking(false);
          return;
        }
        const showId = res.decided.showId;
        if (!showId) return;
        sendInvites.mutate({ showId, people, note }, {
          onSuccess: () => sent(people.length),
          // The hold exists now; keep the picks on screen against it so Send can be pressed again.
          onError: () => setHeldShow(showId),
        });
      },
    });
  }

  if (crew && desktop) {
    return (
      <ShowEditorModal
        showId={crew}
        step={step}
        onStep={setStep}
        onCreated={() => {}}
        onExit={onClose}
        onDeleted={onClose}
        onSaved={onClose}
      />
    );
  }

  if (picking || crew || heldShow) {
    const existing = heldShow ?? crew;
    return (
      <DetailsFrame title="Who's playing" anchor={anchor} onClose={onClose} wide dirty={dirty}>
        <WhosPlaying
          venue={venue}
          date={day}
          draft={draft}
          onDraft={setDraft}
          onBack={existing ? undefined : () => setPicking(false)}
          onClose={onClose}
          sending={decide.isPending || sendInvites.isPending}
          onSend={(people, note) =>
            existing
              ? sendInvites.mutate({ showId: existing, people, note }, { onSuccess: () => sent(people.length) })
              : holdAndInvite(people, note)}
        />
      </DetailsFrame>
    );
  }

  const title = q.data?.venueName || q.data?.buyerVenue || "Show request";
  const body = q.isLoading ? (
    <div className={base.loading} role="status" aria-live="polite"><Spinner /></div>
  ) : q.isError || !q.data ? (
    <div className={base.loading}>
      <p className={base.error}>Couldn&apos;t load this request.</p>
      <button type="button" className={base.close} onClick={onClose} aria-label="Close">
        <Icon icon={X} size={20} />
      </button>
    </div>
  ) : (
    <RequestBody request={q.data} onClose={onClose} onCrew={setCrew} onPick={() => setPicking(true)} />
  );
  return <DetailsFrame title={title} anchor={anchor} onClose={onClose} wide>{body}</DetailsFrame>;
}

function RequestBody({ request: r, onClose, onCrew, onPick }: {
  request: ShowRequestView;
  onClose: () => void;
  onCrew: (showId: string) => void;
  /** Hold & add crew: open Who's playing. The request is answered only when its invites are sent. */
  onPick: () => void;
}) {
  const decide = useDecideShowRequest();
  const [passing, setPassing] = useState(false);
  const [reason, setReason] = useState("");
  const agent = firstName(r.sentBy?.name);
  const days = offerDaysLeft(r.goodThrough);
  const open = r.status === "requested" && !r.decision;
  const sub = [
    [r.city, r.state].filter(Boolean).join(", ") || null,
    r.date ? longDay(r.date) : null,
    r.capacity ? `${r.capacity.toLocaleString()} cap` : null,
    r.holdTier ? HOLD_TIER[r.holdTier] ?? `${r.holdTier}th hold` : null,
  ].filter(Boolean).join(" · ");

  function answer(action: ShowRequestAction) {
    decide.mutate({ offerId: r.offerId!, action, reason: action === "pass" ? reason.trim() || null : null }, {
      onSuccess: (res) => {
        if (res.conflict) {
          const who = res.conflict.decidedBy?.name ?? "Someone";
          pushToast(`${who} already ${res.conflict.action === "pass" ? "passed on" : res.conflict.action === "hold" ? "held" : "approved"} this.`);
          return;
        }
        if (action === "approve") pushToast(agent ? `Approved. ${agent} will close it with the venue.` : "Approved.", "success");
        else if (action === "pass") onClose();
      },
    });
  }

  return (
    <>
      <div className={base.scroll}>
        <header className={base.head}>
          <div className={base.headRow}>
            <span className={`${base.kindSquare} ${base.kind}`} data-kind="hold" aria-hidden />
            <span className={base.kindLabel}>Show request</span>
            {r.goodThrough && open && (
              <span className={base.pill} data-tone={days != null && days <= URGENT_DAYS ? "urgent" : "invited"}>
                Reply by {formatDay(r.goodThrough)}{days != null && days >= 0 ? ` · ${days}d` : ""}
              </span>
            )}
            <button type="button" className={base.close} onClick={onClose} aria-label="Close">
              <Icon icon={X} size={20} />
            </button>
          </div>
          <h2 className={base.title}>{r.venueName || r.buyerVenue || "TBD"}</h2>
          {sub && <p className={base.date}>{sub}</p>}
        </header>

        <div className={base.sections}>
          {r.noteToTeam && (
            <section className={styles.note} aria-label="Note from the agent">
              <p className={styles.noteFrom}>
                {[r.sentBy?.name, r.sentBy?.org, ago(r.sentOn)].filter(Boolean).join(" · ")}
              </p>
              <p className={styles.noteText}>{r.noteToTeam}</p>
            </section>
          )}

          <div className={styles.pair}>
            {r.terms && <Terms terms={r.terms} />}
            {(r.lineup?.length ?? 0) > 0 || r.contractMediaId ? (
              <Block label="Lineup">
                <ul className={styles.lineup}>
                  {(r.lineup ?? []).map((s, i) => (
                    <li key={`${s.artistName}-${i}`}>
                      <span className={styles.slot}>{slotRoleLabel(s.slotRole) ?? "Slot"}</span>
                      <span className={base.strong}>{s.artistName}</span>
                      {s.minutes ? <span className={base.sub}> · {s.minutes}m</span> : null}
                    </li>
                  ))}
                </ul>
                {r.contractMediaId && <Contract mediaId={r.contractMediaId} name={r.contractFileName} />}
              </Block>
            ) : null}
          </div>

          {r.date && <CalendarCheck date={r.date} />}
        </div>
      </div>

      <footer className={base.actions}>
        {!open ? (
          <Decided request={r} agent={agent} onOpen={r.showId && r.decision !== "pass" ? () => onCrew(r.showId!) : null} />
        ) : passing ? (
          <>
            <label className={styles.reasonLabel} htmlFor="request-pass-reason">Why pass? (optional — {agent ?? "the agent"} sees it)</label>
            <textarea
              id="request-pass-reason"
              className={styles.reason}
              value={reason}
              maxLength={500}
              rows={2}
              onChange={(e) => setReason(e.target.value)}
            />
            <div className={base.buttons}>
              <button type="button" className={base.btnGhost} onClick={() => setPassing(false)} disabled={decide.isPending}>
                Cancel
              </button>
              <button type="button" className={base.btnDanger} onClick={() => answer("pass")} disabled={decide.isPending}>
                Pass
              </button>
            </div>
          </>
        ) : (
          <>
            <div className={base.buttons}>
              <button type="button" className={base.btnGhost} onClick={() => setPassing(true)} disabled={decide.isPending}>
                Pass
              </button>
              <button type="button" className={base.btnGhost} onClick={onPick} disabled={decide.isPending}>
                Hold &amp; add crew
              </button>
              <span className={styles.tipHost}>
                <button
                  type="button"
                  className={base.btnPrimary}
                  onClick={() => answer("approve")}
                  disabled={decide.isPending}
                  aria-describedby="request-approve-tip"
                >
                  Approve
                </button>
                <span id="request-approve-tip" role="tooltip" className={styles.tip}>
                  Sends to {agent ?? "the agent"} and closes
                </span>
              </span>
            </div>
          </>
        )}
      </footer>
    </>
  );
}

/** "Dana Whitfield approved this · 2h ago · sent to Jules to close" — first response counts. */
function Decided({ request: r, agent, onOpen }: { request: ShowRequestView; agent: string | null; onOpen: (() => void) | null }) {
  const who = r.decidedBy?.name ?? "The team";
  const verb = VERB[r.decision ?? ""] ?? (r.status === "passed" ? "passed" : "answered");
  const c = r.inviteCounts;
  const counts = c
    ? [c.in ? `${c.in} in` : null, c.waiting ? `${c.waiting} waiting` : null, c.out ? `${c.out} out` : null].filter(Boolean).join(" · ")
    : null;
  const detail = r.decision === "approve"
    ? agent ? `sent to ${agent} to close` : null
    : r.decision === "hold" ? counts || "checking with the band"
      : r.decisionReason ? `“${r.decisionReason}”` : null;
  return (
    <div className={styles.decided}>
      <div className={styles.decidedText}>
        <p className={base.strong}>{who} {verb}{verb === "passed" ? "" : " this"}</p>
        <p className={base.sub}>{[ago(r.decidedOn), detail].filter(Boolean).join(" · ")}</p>
      </div>
      {onOpen && (
        <button type="button" className={base.btnGhost} onClick={onOpen}>Open</button>
      )}
    </div>
  );
}

function Terms({ terms: t }: { terms: NonNullable<ShowRequestView["terms"]> }) {
  const versus = t.dealType === "terms" && t.doorSplitPct != null;
  const rows: [string, string][] = [];
  if (t.doorSplitPct != null) {
    rows.push(["Door split", `${t.doorSplitPct}%${t.doorSplitAfterCents ? ` over ${formatMoneyCents(t.doorSplitAfterCents)}` : ""}`]);
  }
  if (t.depositCents != null) {
    rows.push(["Deposit", [formatMoneyCents(t.depositCents), t.depositDueOn ? formatDay(t.depositDueOn) : null].filter(Boolean).join(" · ")]);
  }
  if (t.ticketPriceCents != null) rows.push(["Tickets", formatMoneyCents(t.ticketPriceCents)]);
  return (
    <Block label="Terms">
      {t.amountCents != null && (
        <p className={styles.guarantee}>
          {formatMoneyCents(t.amountCents)} <span className={base.sub}>{versus ? "vs door" : "guarantee"}</span>
        </p>
      )}
      {rows.length > 0 && (
        <dl className={styles.terms}>
          {rows.map(([k, v]) => (
            <div key={k}><dt>{k}</dt><dd>{v}</dd></div>
          ))}
        </dl>
      )}
      {t.contractTerms && <p className={styles.contractTerms}>{t.contractTerms}</p>}
    </Block>
  );
}

function Contract({ mediaId, name }: { mediaId: string; name: string | null | undefined }) {
  const { bandId } = useBandContext();
  const file = useMediaObjectUrl(mediaId, null, { bandId });
  return (
    <p className={styles.contract}>
      <span className={styles.fileTag}>PDF</span>
      <span className={styles.fileName}>{name || "Contract"}</span>
      {file.url ? (
        <a className={base.link} href={file.url} target="_blank" rel="noreferrer">View</a>
      ) : null}
    </p>
  );
}

/** The artist's own calendar two days either side of the requested date, the date itself highlighted. */
function CalendarCheck({ date }: { date: string }) {
  const days = [-2, -1, 0, 1, 2].map((d) => shiftDay(date, d));
  const cal = useOpsCalendar(days[0], days[4]);
  const events = cal.data?.events ?? [];
  return (
    <Block label={`Your calendar around ${formatDay(date)}`}>
      <ol className={styles.strip}>
        {days.map((d) => {
          const on = events.filter((e) => (e.date ?? "").slice(0, 10) === d);
          const label = on.length > 0
            ? (on[0]!.venueName || on[0]!.title || on[0]!.city || on[0]!.type || "Busy")
            : "Open";
          const day = new Date(`${d}T12:00:00`);
          return (
            <li key={d} className={styles.day} data-this={d === date || undefined} data-busy={on.length > 0 || undefined}>
              <span className={styles.dow}>
                {day.toLocaleDateString(undefined, { weekday: "short" }).toUpperCase()} {day.getDate()}
              </span>
              <span className={styles.dayLabel}>{d === date ? "This request" : label}</span>
              {d === date && on.length > 0 && <span className={styles.clash}>Also: {label}</span>}
            </li>
          );
        })}
      </ol>
    </Block>
  );
}

function Block({ label, children }: { label: string; children: ReactNode }) {
  return (
    <section className={styles.block} aria-label={label}>
      <h3 className={styles.blockLabel}>{label}</h3>
      {children}
    </section>
  );
}

function firstName(name: string | null | undefined): string | null {
  return name?.trim().split(/\s+/)[0] || null;
}

function longDay(iso: string): string {
  return new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

/** `YYYY-MM-DD` shifted by whole days, in local wall-clock (never a UTC shift). */
function shiftDay(iso: string, by: number): string {
  const d = new Date(`${iso.slice(0, 10)}T12:00:00`);
  d.setDate(d.getDate() + by);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
